<template>
  <section>
    <audio ref="content" controls preload="none" class="d-none" />
    <playback-control :duration="durationValue"
                      :currentTime="currentTimeValue"
                      :volume="volumeValue"
                      :muted="isMuted"
                      :played="mergedPlayed"
                      :buffered="convertedBuffered"
                      :status="status"
                      v-on:play="play"
                      v-on:pause="pause"
                      v-on:seekStart="seekStart"
                      v-on:seeking="seeking"
                      v-on:seekEnd="seekEnd"
                      v-on:volumeChanged="changeVolume" />
  </section>
</template>

<script>
  import { defineComponent } from "vue";
  import PlaybackControl from "components/control/playback_control";

  export default defineComponent({
    props: {
      src: {
        type: String,
        required: true,
      },
      currentTime: {
        type: Number,
        default: null,
      },
      muted: {
        type: Boolean,
        default: false,
      },
      volume: {
        type: Number,
        default: 1,
        validator: (val) => {
          return 0 <= val && val <= 1;
        },
      },
      alreadyPlayed: {
        type: Array,
        validator: (val) => {
          return val.every(i => Object.keys(i).includes("start") && Object.keys(i).includes("end"));
        }
      },
      seekbarColor: {
        type: String,
        default: "primary",
        validator: (val) => {
          return Object.keys(__COLOR_CODE).includes(val);
        },
      },
    },
    computed: {
      mergedPlayed() {
        if (this.playedValue == null && this.alreadyPlayed == null) return null;
        let convertedPlayed = [];
        for (var i = 0; i < this.playedValue.length; i++) {
          convertedPlayed.push({
            start: this.playedValue.start(i),
            end: this.playedValue.end(i),
          });
        }
        if (convertedPlayed.length === 0) return this.alreadyPlayed;
        if (this.alreadyPlayed == null || this.alreadyPlayed.length === 0) return convertedPlayed;
        let mp = this.alreadyPlayed.concat(convertedPlayed);
        mp = mp.filter((x, xIndex) => !mp.some((y, yIndex) => xIndex !== yIndex && y.start <= x.start && x.end <= y.end));
        mp.forEach((x, xIndex) => {
          let t = mp.findIndex((y, yIndex) => xIndex !== yIndex && y.start <= x.start && x.start <= y.end);
          if (t >= 0) {
            mp[t].end = mp[xIndex].end;
            mp.splice(xIndex, 1);
          }
        });
        mp.forEach((x, xIndex) => {
          let t = mp.findIndex((y, yIndex) => xIndex !== yIndex && y.start <= x.end && x.end <= y.end);
          if (t >= 0) {
            mp[t].start = mp[xIndex].start;
            mp.splice(xIndex, 1);
          }
        });
        mp.sort((prevP, currentP) => prevP.start - currentP.start);
        return mp;
      },
      progress() {
        let val = this.mergedPlayed.reduce(
          (p, c) => p + (c.end - c.start),
          0
        );
        return Math.round(val / this.durationValue * 100);
      },
      convertedBuffered() {
        if (this.bufferedValue == null) return null;
        let convertedBuffered = [];
        for (var i = 0; i < this.bufferedValue.length; i++) {
          convertedBuffered.push({
            start: this.bufferedValue.start(i),
            end: this.bufferedValue.end(i),
          });
        }
        return convertedBuffered;
      },
    },
    data: () => {
      return {
        content: null,
        durationValue: 0,
        currentTimeValue: 0,
        volumeValue: 1,
        isMuted: false,
        status: "paused",
        pausedCurrent: true,
        started: false,
        playedValue: {},
        bufferedValue: {},
        inSeeking: false,
      }
    },
    methods: {
      loadContent() {
        this.$refs.content.src = this.src;
        this.$refs.content.addEventListener("loadedmetadata", function () {
          this.content = this.$refs.content;
          this.handlingEvents();
          this.durationValue = this.content.duration;
          this.content.volume = this.volume;
          this.volumeValue = this.volume;
          this.content.muted = this.muted;
          this.isMuted = this.muted;
        }.bind(this));
        this.$refs.content.load();
      },
      handlingEvents() {
        this.content.addEventListener("timeupdate", function () {
          this.currentTimeValue = this.content.currentTime;
          this.playedValue = this.content.played;
          this.bufferedValue = this.content.buffered;
        }.bind(this));
        this.content.addEventListener("progress", function () {
          this.bufferedValue = this.content.buffered;
        }.bind(this));
        this.content.addEventListener("ended", function () {
          this.status = "ended";
        }.bind(this));
        this.content.addEventListener("pause", function () {
          this.status = "paused";
        }.bind(this));
        this.content.addEventListener("playing", function () {
          if (!this.started) {
            this.started = true;
            this.$emit("start");
            console.log("start");
          }
          this.status = "playing";
        }.bind(this));
        this.content.addEventListener("volumechange", function () {
          this.volumeValue = this.content.volume;
        }.bind(this));
      },
      changeVolume(volumeParam) {
        this.volumeValue = volumeParam.volume;
        this.isMuted = volumeParam.muted;
        this.content.volume = this.volumeValue;
        this.content.muted = this.isMuted;
      },
      seekStart(currentTime) {
        this.inSeeking = true;
        this.pausedCurrent = this.content.paused;
        this.content.pause();
        if (this.status === "ended") {
          this.status = "paused";
        }
        this.content.currentTime = currentTime;
      },
      seeking(currentTime) {
        this.currentTimeValue = currentTime;
        this.content.currentTime = currentTime;
      },
      seekEnd() {
        this.inSeeking = false;
        this.content.currentTime = this.currentTimeValue;
        if (!this.pausedCurrent) this.content.play();
      },
      play() {
        if (this.content) this.content.play();
      },
      pause() {
        if (this.content) this.content.pause();
      },
    },
    beforeCreate() {
    },
    created() {
    },
    mounted() {
      this.loadContent();
    },
    emits: ["start", "playing", "pause", "ended", "completed"],
    components: {
      "playback-control": PlaybackControl,
    },
    watch: {
      src: function () {
        this.content = null;
        this.loadContent();
      },
      progress: function () {
        if (this.progress === 100) {
          console.log("completed")
          this.$emit("completed");
        };
      },
    }
  });

</script>
